/**
 * Utility functions for MemoryPack deserialization.
 * 
 * These are kept separate from the generated NodeDto.ts to avoid
 * being overwritten by the MemoryPack TypeScript generator.
 */

import { MemoryPackReader } from './MemoryPackReader';
import { NodeDto } from './NodeDto';

/**
 * Deserializes multiple concatenated MemoryPack arrays from a buffer.
 * Used for streamed responses where the server sends batches as separate arrays.
 * 
 * @param buffer - ArrayBuffer containing multiple concatenated MemoryPack arrays
 * @returns All nodes from all arrays concatenated, or null if empty
 */
export function deserializeConcatenatedNodeArrays(buffer: ArrayBuffer): NodeDto[] | null {
  const reader = new MemoryPackReader(buffer);
  const allNodes: NodeDto[] = [];

  // Read arrays until we've consumed the entire buffer
  while (!reader.isEnd()) {
    const batch = reader.readArray(r => NodeDto.deserializeCore(r));
    if (batch) {
      for (const node of batch) {
        if (node !== null) {
          allNodes.push(node);
        }
      }
    }
  }

  return allNodes.length > 0 ? allNodes : null;
}

/**
 * Deserializes a single MemoryPack array, filtering out null entries.
 * 
 * @param buffer - ArrayBuffer containing a single MemoryPack array
 * @returns Array of non-null NodeDto objects, or null if empty
 */
export function deserializeNodeArray(buffer: ArrayBuffer): NodeDto[] | null {
  const rawNodes = NodeDto.deserializeArray(buffer);
  if (!rawNodes) return null;

  const nodes = rawNodes.filter((n): n is NodeDto => n !== null);
  return nodes.length > 0 ? nodes : null;
}

